import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";

import { JobMode } from "../../../../../../../models/job.mode";
import { Filters, Searches, Sorts, Sort } from "../../../../../../../models/shipeezi.table";
import ModeJobService from "../../../../../../../services/mode.job.service";
import { useAppState } from "../../../../../../../context/StateContext";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";

const useActions = () => {
  const { currentUser } = useAppState();

  const [modeTypes, setModeTypes] = useState<JobMode[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean | undefined>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();

  const { refetch, isFetching } = useQuery(
    ["mode.list", page, pageSize, filters, searches, sorting],
    () =>
      ModeJobService.getAll({
        companyId: currentUser?.companyId,
        page,
        pageSize,
        sortBy: sorting
          ? JSON.stringify([...sorting, ["name", "asc"]])
          : JSON.stringify([["name", "asc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setModeTypes(response.data);
      },
    }
  );

  const { refetch: refetchModeTotal } = useQuery(
    ["mode.count", filters, searches],
    () =>
      ModeJobService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
        else setTotal(0);
      },
    }
  );

  const { mutate: updateMode, isLoading: isUpdating } = useMutation(
    ["mode.update"],
    ({ id, mode }: { id: number; mode: JobMode }) => ModeJobService.update(id, mode),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchModeTotal();
      },
      onError: (error: any) => {
        NotificationToast.error(error.response.data.message);
        setHasErrors(true);
      },
    }
  );

  const { mutate: createMode, isLoading: isCreating } = useMutation(
    ["mode.create"],
    (mode: JobMode) => ModeJobService.create(mode),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchModeTotal();
      },
      onError: (error: any) => {
        NotificationToast.error(error.response.data.message);
        setHasErrors(true);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    modeTypes,
    total,
    page,
    pageSize,
    hasErrors,
    isLoading: isFetching || isCreating || isUpdating,
    setPageSize,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createMode,
    updateMode,
  };
};

export default useActions;
